import { addDays, compareLocalDate, diffDays } from '../../recurrence/localDate.js';
import { ADAR, ADAR_II, AV, dayOfWeek, fromHebrew, IYAR, KISLEV, lastMonthOfYear, NISAN, SHEVAT, SIVAN, TAMMUZ, TEVET, TISHRI, toHebrew, } from './hebrewCalendar.js';
// Joodse feest- en vastendagen, uitgerekend met de eigen kalender (hebrewCalendar.ts). Bronnen
// voor de regels: Sjoelchan Aroech, Orach Chajim (o.a. §428 voor de vaste weekdagen, §549-550 en
// §686 voor het verschuiven van vastendagen); praktisch nagelopen tegen Hebcal (www.hebcal.com,
// CC BY 4.0) voor 2019-2040, diaspora en Israël, in holidays.test.ts.
//
// - Diaspora (buiten Israël; ook Nederland): de tweede dag van Rosj Hasjana geldt overal; Soekot,
//   Pesach en Sjavoeot hebben er een tweede feestdag (jom tov sjeni sjel galoejot); Simchat Tora
//   is de dag na Sjemini Atseret. In Israël vallen Sjemini Atseret en Simchat Tora samen.
// - `eve`: de dag begint de avond ervoor bij zonsondergang (feestdagen en Tisja Beav). De kleine
//   vastendagen (Tsom Gedalja, Asara Betevet, Taaniet Esther, 17 Tamoez) beginnen bij het
//   ochtendgloren: geen avond ervoor.
// - Vastendagen die op sjabbat vallen, schuiven: Tsom Gedalja, 17 Tamoez en Tisja Beav naar
//   zondag, Taaniet Esther naar de donderdag ervoor. Asara Betevet valt nooit op sjabbat.
//
// LET OP (review): namen, regels en uitleg vóór de publieke lancering laten nakijken door een
// rabbijn of een lid van de gemeenschap.
export const JEWISH_HOLIDAY_KEYS = [
    'rosh_hashana',
    'tzom_gedalia',
    'yom_kippur',
    'sukkot',
    'shemini_atzeret',
    'simchat_torah',
    'chanukah',
    'asara_btevet',
    'tu_bishvat',
    'taanit_esther',
    'purim',
    'pesach',
    'lag_baomer',
    'shavuot',
    'tzom_tammuz',
    'tisha_bav',
];
function day(key, h, kind, extra) {
    return {
        key,
        date: fromHebrew(h),
        hebrew: h,
        kind,
        dayIndex: extra.dayIndex ?? 1,
        dayCount: extra.dayCount ?? 1,
        eve: extra.eve ?? false,
        yomTov: extra.yomTov ?? false,
        cholHamoed: extra.cholHamoed ?? false,
    };
}
/** Een vastendag die op sjabbat valt, schuift `shift` dagen (zondag +1, Esther −2). */
function fastDate(h, shift) {
    const date = fromHebrew(h);
    if (dayOfWeek(date) !== 6)
        return h;
    return toHebrew(addDays(date, shift));
}
/**
 * Alle feest- en vastendagen van joods jaar `year` (van Rosj Hasjana tot en met Tisja Beav), op
 * datum gesorteerd. Meerdaagse feesten komen één keer per dag.
 */
export function jewishHolidaysOfYear(year, opts) {
    const out = [];
    const israel = opts.israel;
    const at = (month, d) => ({ year, month, day: d });
    // Rosj Hasjana: twee dagen, ook in Israël.
    for (let i = 0; i < 2; i += 1) {
        out.push(day('rosh_hashana', at(TISHRI, 1 + i), 'major', {
            dayIndex: i + 1,
            dayCount: 2,
            eve: i === 0,
            yomTov: true,
        }));
    }
    out.push(day('tzom_gedalia', fastDate(at(TISHRI, 3), 1), 'fast', {}));
    out.push(day('yom_kippur', at(TISHRI, 10), 'major', { eve: true, yomTov: true }));
    // Soekot: 15-21 Tisjri (21 = Hosjana Rabba). Feestdag 15, in de diaspora ook 16.
    for (let i = 0; i < 7; i += 1) {
        const yt = i === 0 || (i === 1 && !israel);
        out.push(day('sukkot', at(TISHRI, 15 + i), 'major', {
            dayIndex: i + 1,
            dayCount: 7,
            eve: i === 0,
            yomTov: yt,
            cholHamoed: !yt,
        }));
    }
    out.push(day('shemini_atzeret', at(TISHRI, 22), 'major', { eve: true, yomTov: true }));
    // Israël: Simchat Tora valt op Sjemini Atseret (de API toont dan één dag met beide namen).
    if (!israel) {
        out.push(day('simchat_torah', at(TISHRI, 23), 'major', { yomTov: true }));
    }
    // Chanoeka: 25 Kislev, acht dagen; het eerste lichtje de avond ervoor.
    const chanukahStart = fromHebrew(at(KISLEV, 25));
    for (let i = 0; i < 8; i += 1) {
        const date = addDays(chanukahStart, i);
        out.push({
            key: 'chanukah',
            date,
            hebrew: toHebrew(date),
            kind: 'minor',
            dayIndex: i + 1,
            dayCount: 8,
            eve: i === 0,
            yomTov: false,
            cholHamoed: false,
        });
    }
    out.push(day('asara_btevet', at(TEVET, 10), 'fast', {}));
    out.push(day('tu_bishvat', at(SHEVAT, 15), 'minor', { eve: true }));
    // Poeriem in Adar (gewoon jaar) of Adar II (schrikkeljaar).
    const adar = lastMonthOfYear(year) === ADAR_II ? ADAR_II : ADAR;
    out.push(day('taanit_esther', fastDate(at(adar, 13), -2), 'fast', {}));
    out.push(day('purim', at(adar, 14), 'minor', { eve: true }));
    // Pesach: 15-22 Nisan in de diaspora (8 dagen), 15-21 in Israël (7). Feestdagen: de eerste en de
    // zevende, in de diaspora ook de tweede en de achtste.
    const pesachDays = israel ? 7 : 8;
    for (let i = 0; i < pesachDays; i += 1) {
        const yt = israel ? i === 0 || i === 6 : i <= 1 || i >= 6;
        out.push(day('pesach', at(NISAN, 15 + i), 'major', {
            dayIndex: i + 1,
            dayCount: pesachDays,
            eve: i === 0,
            yomTov: yt,
            cholHamoed: !yt,
        }));
    }
    out.push(day('lag_baomer', at(IYAR, 18), 'minor', { eve: true }));
    // Sjavoeot: 6 Siwan, in de diaspora ook 7.
    const shavuotDays = israel ? 1 : 2;
    for (let i = 0; i < shavuotDays; i += 1) {
        out.push(day('shavuot', at(SIVAN, 6 + i), 'major', {
            dayIndex: i + 1,
            dayCount: shavuotDays,
            eve: i === 0,
            yomTov: true,
        }));
    }
    out.push(day('tzom_tammuz', fastDate(at(TAMMUZ, 17), 1), 'fast', {}));
    out.push(day('tisha_bav', fastDate(at(AV, 9), 1), 'fast', { eve: true }));
    return out.sort((a, b) => compareLocalDate(a.date, b.date));
}
/** Feest- en vastendagen tussen `from` en `to` (inclusief), over jaargrenzen heen. */
export function jewishHolidaysBetween(from, to, opts) {
    const y0 = toHebrew(from).year;
    const y1 = toHebrew(to).year;
    const out = [];
    for (let y = y0; y <= y1; y += 1) {
        for (const d of jewishHolidaysOfYear(y, opts)) {
            if (compareLocalDate(d.date, from) >= 0 && compareLocalDate(d.date, to) <= 0)
                out.push(d);
        }
    }
    return out;
}
/** Is `date` een rustdag: sjabbat of een jom tov? */
export function isRestDay(date, opts) {
    if (dayOfWeek(date) === 6)
        return true;
    return jewishHolidaysBetween(date, date, opts).some((d) => d.yomTov);
}
/**
 * Omer: dag 1 is 16 Nisan (geteld op de avond van 15 Nisan, na de eerste seder), dag 49 is
 * 5 Siwan. Geeft het getal van de joodse dag van `date` (overdag), of 0 buiten de omer.
 */
export function omerDay(date) {
    const h = toHebrew(date);
    const start = fromHebrew({ year: h.year, month: NISAN, day: 16 });
    const n = diffDays(start, date);
    return n >= 0 && n < 49 ? n + 1 : 0;
}
/**
 * Chanoeka: hoeveel lichtjes er de avond van `date` (na zonsondergang) branden: 1 op de avond van
 * 24 Kislev ... 8 op de avond vóór de achtste dag. 0 buiten Chanoeka (ook op de achtste dag zelf).
 */
export function chanukahCandlesTonight(date) {
    const h = toHebrew(addDays(date, 1));
    const start = fromHebrew({ year: h.year, month: KISLEV, day: 25 });
    const n = diffDays(start, addDays(date, 1));
    return n >= 0 && n < 8 ? n + 1 : 0;
}
/** De Chanoekadag van `date` (overdag): 1..8, anders 0. */
export function chanukahDay(date) {
    const h = toHebrew(date);
    const start = fromHebrew({ year: h.year, month: KISLEV, day: 25 });
    const n = diffDays(start, date);
    return n >= 0 && n < 8 ? n + 1 : 0;
}
//# sourceMappingURL=holidays.js.map